import { useEffect, useState, useContext, useMemo } from "react"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import { useNavigate, useLocation } from "react-router-dom"
import {
  Box,
  Paper,
  TextField,
  Tooltip,
  IconButton,
  Grid,
  Typography,
  Autocomplete,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useMutation, useQuery, api, showWarning } from "../../../api"
import { GlobalContext } from "../../../contexts/GlobalContext"
import getIconComponent from "../../utils/getIconComponent"
import Save from "@mui/icons-material/Save"
import SearchIcon from "@mui/icons-material/Search"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const EditarTipoTransacciones = () => {
  const navigate = useNavigate()
  const { state } = useLocation()
  const { selectedMenuInfo } = useContext(GlobalContext)

  const [tracodigoViejo, setTracodigoViejo] = useState("")

  const [formData, setFormData] = useState({
    tracodigo: "",
    tradescri: "",
    tratipo: "INGRESO",
    trasigno: "+",
    trametodo: false,
    trastagar: false,
    trastaser: false,
    trastasis: false,
    pctacodigo: "",
    tipocodigo: null,
    tsolcodigo: null,
    tralimite: 0,
    trastatus: "A",
  })

  const [openModalCuentas, setOpenModalCuentas] = useState(false)
  const [filtroNivel, setFiltroNivel] = useState("")
  const [busquedaCuenta, setBusquedaCuenta] = useState("")

  useEffect(() => {
    if (state) {
      setFormData({
        tracodigo: state.tracodigo || "",
        tradescri: state.tradescri || "",
        tratipo: state.tratipo || "INGRESO",
        trasigno: state.trasigno || "+",
        trametodo: Boolean(state.trametodo),
        trastagar: Boolean(state.trastagar),
        trastaser: state.trastaser === "1" || state.trastaser === true,
        trastasis: state.trastasis === "1" || state.trastasis === true,
        pctacodigo: state.pctacodigo || "",
        tipocodigo: state.tipocodigo || null,
        tsolcodigo: state.tsolcodigo || null,
        tralimite: state.tralimite || 0,
        trastatus: state.trastatus || "A",
      })
      setTracodigoViejo(state.tracodigo || "")
    } else {
      navigate(-1)
    }
  }, [state, navigate])

  const { data: rawTipoOC, isLoading: isLoadingOC } = useQuery({
    queryKey: ["listaTipoOC_Editar"],
    queryFn: async () => {
      try {
        const response = await api.get("/TipoTransacciones/getListaTipoOC")
        return response?.data?.data || response?.data || response || []
      } catch (error) {
        return []
      }
    },
  })

  const ocValidadas = Array.isArray(rawTipoOC) ? rawTipoOC : Array.isArray(rawTipoOC?.data) ? rawTipoOC.data : []
  const listaOC = ocValidadas.map((item) => ({
    id: item.tipocodigo,
    label: `${item.tipocodigo} - ${item.tipodescri || ""}`,
  }))

  const { data: rawTipoSol, isLoading: isLoadingSol } = useQuery({
    queryKey: ["listaTipoSol_Editar"],
    queryFn: async () => {
      try {
        const response = await api.get("/TipoTransacciones/getListaTipoSol")
        return response?.data?.data || response?.data || response || []
      } catch (error) {
        return []
      }
    },
  })

  const solValidadas = Array.isArray(rawTipoSol) ? rawTipoSol : Array.isArray(rawTipoSol?.data) ? rawTipoSol.data : []
  const listaSol = solValidadas.map((item) => ({
    id: item.tsolcodigo,
    label: `${item.tsolcodigo} - ${item.tsoldescripcion || ""}`,
    tsolInEg: item.tsolInEg,
  }))

  const { data: rawCuentas, isLoading: isLoadingCuentas } = useQuery({
    queryKey: ["listaCuentasContables_Editar"],
    queryFn: async () => {
      try {
        const response = await api.get("/TipoTransacciones/getListaCuentas")
        // Retornamos el objeto completo
        return response?.data || response || {}
      } catch (error) {
        return {}
      }
    },
  })

  // 💡 EXTRACCIÓN A PRUEBA DE BALAS
  const cuentasValidadas = Array.isArray(rawCuentas)
    ? rawCuentas
    : Array.isArray(rawCuentas?.data)
      ? rawCuentas.data
      : Array.isArray(rawCuentas?.data?.data)
        ? rawCuentas.data.data
        : []

  const nivelesMaximos = rawCuentas?.nivelesMaximos || rawCuentas?.data?.nivelesMaximos || 5

  const cuentasFiltradas = useMemo(() => {
    return cuentasValidadas.filter((c) => {
      const matchNivel = filtroNivel ? String(c.pctanivel) === String(filtroNivel) : true
      const codigo = c.pctacodigo || ""
      const nombre = c.pctanomcta || ""
      const matchBusqueda =
        codigo.includes(busquedaCuenta) || nombre.toLowerCase().includes(busquedaCuenta.toLowerCase())
      return matchNivel && matchBusqueda
    })
  }, [cuentasValidadas, filtroNivel, busquedaCuenta])

  const cuentaSeleccionada = cuentasValidadas.find((c) => c.pctacodigo === formData.pctacodigo)
  const displayCuenta = cuentaSeleccionada
    ? `${cuentaSeleccionada.pctacodigo} - ${cuentaSeleccionada.pctanomcta}`
    : formData.pctacodigo

  const naturalezaRequerida = formData.trasigno === "+" ? "SOLING" : "SOLEGR"
  const listaSolFiltrada = listaSol.filter((s) => s.tsolInEg === naturalezaRequerida)

  useEffect(() => {
    if (formData.tratipo === "COSTOS") {
      setFormData((prev) => ({
        ...prev,
        trasigno: "+",
        trametodo: true,
        trastasis: true,
        trastagar: false,
        trastaser: false,
        pctacodigo: "",
        tipocodigo: null,
      }))
    } else if (formData.tratipo === "INGRESO" || formData.tratipo === "ING.PRO" || formData.tratipo === "REINGR.") {
      setFormData((prev) => ({ ...prev, trasigno: "+", tipocodigo: null }))
    } else if (
      formData.tratipo === "EGRESO" ||
      formData.tratipo === "EGR.PRO" ||
      formData.tratipo === "EGR.FAC" ||
      formData.tratipo === "DEV.PRO" ||
      formData.tratipo === "EGR.ANC"
    ) {
      setFormData((prev) => ({
        ...prev,
        trasigno: "-",
        trametodo: false,
        tipocodigo: null,
        pctacodigo: formData.tratipo === "EGR.FAC" ? "" : prev.pctacodigo,
      }))
    } else if (formData.tratipo === "COMPRA") {
      setFormData((prev) => ({ ...prev, trasigno: "+", pctacodigo: "" }))
    }
  }, [formData.tratipo])

  const {
    mutateAsync: SaveEdicionTransaccion,
    isPending: isSavingEdicion,
    isError,
  } = useMutation({
    queryKey: ["isEditingTipoTransaccion"],
    fn: async (data) => {
      const response = await api.post("/TipoTransacciones/updateTipoTransacciones", data)
      return response.data
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: () => navigate(-1),
  })

  const handleInputChange = (field, value) => {
    let finalValue = value

    if (typeof value === "string" && field !== "trastatus") {
      finalValue = value.toUpperCase()
      if (field === "tracodigo") finalValue = finalValue.slice(0, 3)
      else if (field === "tradescri") finalValue = finalValue.slice(0, 30)
    }

    setFormData((prev) => ({ ...prev, [field]: finalValue }))
  }

  const handleSubmit = async (e) => {
    if (e) e.preventDefault()

    const payload = {
      tracodigoOld: tracodigoViejo,
      tracodigoNew: formData.tracodigo,
      tradescri: formData.tradescri,
      tratipo: formData.tratipo,
      trasigno: formData.trasigno,
      trametodo: formData.trametodo,
      trastagar: formData.trastagar,
      trastaser: formData.trastaser,
      trastasis: formData.trastasis,
      pctacodigo: formData.pctacodigo,
      tipocodigo: formData.tipocodigo,
      tsolcodigo: formData.tsolcodigo,
      tralimite: formData.tralimite,
      trastatus: formData.trastatus,
    }

    try {
      if (!payload.tracodigoNew.trim()) return showWarning("El Código de Transacción es obligatorio")
      if (!payload.tradescri.trim()) return showWarning("La Descripción de la Transacción es obligatoria")
      if (!payload.tratipo) return showWarning("Debe seleccionar un Tipo de Transacción")

      if (payload.tratipo === "COMPRA" && !payload.tipocodigo) {
        return showWarning("Debe seleccionar un Tipo de Orden de Compra para transacciones de COMPRA")
      }

      if (payload.tratipo !== "COMPRA" && payload.tratipo !== "EGR.FAC") {
        if (!payload.pctacodigo.trim()) {
          return showWarning("La Cuenta Contable es obligatoria para este tipo de transacción")
        }
      }

      await SaveEdicionTransaccion(payload)
    } catch (error) {
      console.error("Error al editar el Tipo de Transacción:", error)
      if (error?.code === "VALIDATION_ERROR") {
        showWarning(error.message)
      }
    }
  }

  const actualizarAction = selectedMenuInfo?.data?.barraAcciones?.find((action) => action?.acccaption === "GRABAR")

  const toolbarActions = actualizarAction
    ? [
        {
          label: actualizarAction.acccaption,
          key: actualizarAction.acccaption,
          icon: getIconComponent(actualizarAction.accnameicono, actualizarAction.acctipoico),
        },
      ]
    : [{ label: "Actualizar", key: "ACTUALIZAR", icon: <Save /> }]

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <Box sx={{ mb: 2 }}>
          {toolbarActions.map((action) => (
            <Tooltip title={action.label} key={action.key}>
              <IconButton
                onClick={handleSubmit}
                disabled={isSavingEdicion || isLoadingOC || isLoadingSol || isLoadingCuentas}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, mr: 1 }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Editar Tipo de Transacción: {tracodigoViejo}</b>
        </div>

        <CustomBackdrop isLoading={isSavingEdicion || isLoadingOC || isLoadingSol || isLoadingCuentas} />

        <Box sx={StyledRoot}>
          <Paper
            elevation={3}
            sx={{ p: 4, borderRadius: 3, background: "white" }}
            component="form"
            onSubmit={handleSubmit}
          >
            <Typography variant="h6" color="primary" gutterBottom>
              Datos del Tipo de Transacción
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={3}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Código *"
                  value={formData.tracodigo}
                  onChange={(e) => handleInputChange("tracodigo", e.target.value)}
                  error={isError && !formData.tracodigo}
                  inputProps={{ maxLength: 3 }}
                />
              </Grid>

              <Grid item xs={12} sm={9}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Descripción *"
                  value={formData.tradescri}
                  onChange={(e) => handleInputChange("tradescri", e.target.value)}
                  error={isError && !formData.tradescri}
                  inputProps={{ maxLength: 30 }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Tipo de Transacción *"
                  value={formData.tratipo}
                  onChange={(e) => handleInputChange("tratipo", e.target.value)}
                >
                  <MenuItem value="INGRESO">INGRESO</MenuItem>
                  <MenuItem value="EGRESO">EGRESO</MenuItem>
                  <MenuItem value="COMPRA">COMPRA</MenuItem>
                  <MenuItem value="TOM.FIS">TOM.FIS</MenuItem>
                  <MenuItem value="EGR.PRO">EGR.PRO</MenuItem>
                  <MenuItem value="ING.PRO">ING.PRO</MenuItem>
                  <MenuItem value="EGR.FAC">EGR.FAC</MenuItem>
                  <MenuItem value="DEV.PRO">DEV.PRO</MenuItem>
                  <MenuItem value="REINGR.">REINGR.</MenuItem>
                  <MenuItem value="COSTOS">COSTOS</MenuItem>
                  <MenuItem value="EGR.ANC">EGR.ANC</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={2}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Signo (+/-) *"
                  value={formData.trasigno}
                  disabled={formData.tratipo !== "TOM.FIS"}
                  onChange={(e) => handleInputChange("trasigno", e.target.value)}
                  inputProps={{ maxLength: 1 }}
                />
              </Grid>

              {formData.tratipo === "COMPRA" && (
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={listaOC}
                    getOptionLabel={(option) => option.label || ""}
                    value={listaOC.find((c) => c.id === formData.tipocodigo) || null}
                    onChange={(event, newValue) =>
                      setFormData((prev) => ({ ...prev, tipocodigo: newValue ? newValue.id : null }))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Tipo de Orden de Compra *"
                        placeholder="Seleccione tipo OC..."
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={listaSolFiltrada}
                  getOptionLabel={(option) => option.label || ""}
                  value={listaSolFiltrada.find((s) => s.id === formData.tsolcodigo) || null}
                  onChange={(event, newValue) =>
                    setFormData((prev) => ({ ...prev, tsolcodigo: newValue ? newValue.id : null }))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={`Tipo de Solicitud (${naturalezaRequerida})`}
                      placeholder="Seleccione solicitud..."
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>

              {formData.tratipo !== "COMPRA" && formData.tratipo !== "EGR.FAC" && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    label="Cuenta Contable *"
                    value={displayCuenta}
                    onClick={() => setOpenModalCuentas(true)}
                    InputProps={{ readOnly: true, endAdornment: <SearchIcon color="action" /> }}
                    sx={{ cursor: "pointer", "& .MuiInputBase-root": { cursor: "pointer" } }}
                    placeholder="Clic para seleccionar cuenta en el árbol..."
                    error={isError && !formData.pctacodigo}
                  />
                </Grid>
              )}

              <Grid item xs={12} sm={3}>
                <TextField
                  type="number"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Límite Físico"
                  value={formData.tralimite}
                  onChange={(e) => handleInputChange("tralimite", e.target.value)}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  select
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Estado"
                  value={formData.trastatus}
                  onChange={(e) => handleInputChange("trastatus", e.target.value)}
                >
                  <MenuItem value="A">ACTIVO</MenuItem>
                  <MenuItem value="I">INACTIVO</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} container spacing={2} sx={{ mt: 1 }}>
                <Grid item>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.trametodo}
                        disabled={formData.trasigno === "-" || formData.tratipo === "COSTOS"}
                        onChange={(e) => setFormData((prev) => ({ ...prev, trametodo: e.target.checked }))}
                      />
                    }
                    label="Aplica Promedio de Costo"
                  />
                </Grid>
                <Grid item>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.trastagar}
                        disabled={formData.tratipo === "COSTOS"}
                        onChange={(e) => setFormData((prev) => ({ ...prev, trastagar: e.target.checked }))}
                      />
                    }
                    label="Aplica Garantía"
                  />
                </Grid>
                <Grid item>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.trastaser}
                        onChange={(e) => setFormData((prev) => ({ ...prev, trastaser: e.target.checked }))}
                      />
                    }
                    label="Aplica Series"
                  />
                </Grid>
                <Grid item>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.trastasis}
                        disabled={formData.tratipo === "COSTOS"}
                        onChange={(e) => setFormData((prev) => ({ ...prev, trastasis: e.target.checked }))}
                      />
                    }
                    label="Transacción de Sistema"
                  />
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        </Box>

        {/* Modal Interactivo de Árbol de Cuentas */}
        <Dialog open={openModalCuentas} onClose={() => setOpenModalCuentas(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ bgcolor: "primary.main", color: "white" }}>Estructura del Plan de Cuentas</DialogTitle>
          <DialogContent dividers sx={{ bgcolor: "#fafafa" }}>
            <Grid container spacing={2} sx={{ mb: 2, mt: 1 }}>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  size="small"
                  label="Buscar por código o nombre..."
                  value={busquedaCuenta}
                  onChange={(e) => setBusquedaCuenta(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Filtrar por Nivel de Profundidad"
                  value={filtroNivel}
                  onChange={(e) => setFiltroNivel(e.target.value)}
                >
                  <MenuItem value="">TODOS LOS NIVELES</MenuItem>
                  {Array.from({ length: nivelesMaximos }, (_, i) => i + 1).map((n) => (
                    <MenuItem key={n} value={n}>
                      Nivel {n}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
            <Box sx={{ maxHeight: 400, overflow: "auto", border: "1px solid #ddd", borderRadius: 1, bgcolor: "white" }}>
              {cuentasFiltradas.map((cuenta) => {
                const isTransaccional = String(cuenta.pctatipo).toUpperCase() === "T"
                return (
                  <Box
                    key={cuenta.pctacodigo}
                    onClick={() => {
                      if (isTransaccional) {
                        setFormData((prev) => ({ ...prev, pctacodigo: cuenta.pctacodigo }))
                        setOpenModalCuentas(false)
                      } else {
                        showWarning("Solo puede seleccionar cuentas de movimiento (Transaccionales).")
                      }
                    }}
                    sx={{
                      display: "flex",
                      p: 1.5,
                      cursor: isTransaccional ? "pointer" : "not-allowed", // Cursor cambia en Cuentas de Mayor
                      "&:hover": { bgcolor: isTransaccional ? "#e3f2fd" : "transparent" },
                      pl: cuenta.pctanivel * 3, // Efecto de indentación estructurada (Árbol)
                      borderBottom: "1px solid #f0f0f0",
                      opacity: isTransaccional ? 1 : 0.7,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: !isTransaccional ? "bold" : "normal",
                        color: isTransaccional ? "#333" : "#196C87", // Azul principal para diferenciar cuentas de Mayor
                      }}
                    >
                      {cuenta.pctacodigo} - {cuenta.pctanomcta} {!isTransaccional && "(Mayor)"}
                    </Typography>
                  </Box>
                )
              })}
              {cuentasFiltradas.length === 0 && (
                <Typography sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
                  No se encontraron cuentas contables.
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenModalCuentas(false)} color="inherit">
              Cancelar
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </ThemeProvider>
  )
}

export default EditarTipoTransacciones
